import { createRef, useCallback, useEffect, useMemo, useState } from 'react';

export interface ISliderSizeParams<TItem> {
  slides: TItem[];
  gap: number;
}

export function useScrollableSliderSize<TItem>({ slides, gap }: ISliderSizeParams<TItem>) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  const refsDict = useMemo(() => {
    return slides.reduce<Record<number, React.RefObject<HTMLDivElement>>>((dict, _item, index) => {
      dict[index] = createRef();

      return dict;
    }, {});
  }, [slides]);

  const updateSize = useCallback(() => {
    const newSize = Object.values(refsDict).reduce(
      (size, ref) => {
        const refEl = ref.current;

        /* istanbul ignore next */
        if (!refEl) {
          return size;
        }

        const height = refEl.offsetHeight;

        if (height > size.height) {
          return { width: refEl.offsetWidth, height };
        }

        return size;
      },
      { width: 0, height: 0 },
    );

    setSize({ width: newSize.width + gap, height: newSize.height });
  }, [refsDict, gap]);

  useEffect(() => {
    // setTimeout необходим, чтобы слайды успели отрендериться в актуальном размере
    // для корректности расчетов
    setTimeout(updateSize);

    window.addEventListener('resize', updateSize);

    return () => window.removeEventListener('resize', updateSize);
  }, [updateSize, gap]);

  return { refsDict, size };
}
